import IconButton from "components/atoms/button/IconButton";
import PrimaryDatePicker from "components/molecules/datepicker/PrimaryDatePicker";
import { useAllCampaigns } from "lib/hooks/queries/Campaign/useAllCampaigns";
import { useAllSources } from "lib/hooks/queries/Source/useAllSources";
import React, { Dispatch, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Creatable from "react-select/creatable";
import { customStyles } from "lib/helpers/configs/customStyles";
import LoadingScreen from "components/atoms/loading-screen/LoadingScreen";
import Label from "components/atoms/input/input-label/InputLabel";
import { getUsersByPermission } from "api/User";
import ConditionalRender from "components/atoms/conditional-render/ConditionalRender";
import { useTranslation } from "react-i18next";
import RemoveFIlter from "components/atoms/remove-filter/RemoveFIlter";

export interface IFilterProps {
  source?: any;
  campaign?: any;
  status?: string;
  worker?: string;
  from_date_of_creation?: any;
  to_date_of_creation?: any;
}

const initialState: IFilterProps = {
  source: "",
  campaign: "",
  status: "",
  worker: "",
  from_date_of_creation: null,
  to_date_of_creation: null
};

export interface IFilterTableDropdownProps {
  setFilterData?: (args: any) => void;
  FilterData?: IFilterProps;
  setIsOpen?: Dispatch<React.SetStateAction<boolean>>;
  isAssignedLeads?: boolean;
}

const FilterTableDropdown: React.FC<IFilterTableDropdownProps> = ({
  setFilterData,
  FilterData,
  setIsOpen,
  isAssignedLeads
}) => {

  const { control, reset, watch, getValues, handleSubmit } = useForm({
    defaultValues: FilterData || initialState
  });


  const handleButtonClick = (data: any) => {
    if (setFilterData) {
      setFilterData(data)
    }
    setIsOpen && setIsOpen(false);
  };

  const ResetData = () => {
    reset(initialState);
  };

  const campaigns = useAllCampaigns();
  const sources = useAllSources();

  const [fetchedUsers, setFetchedUsers] = useState<Array<string>>([]);

  useEffect(() => {
    if (!isAssignedLeads) return;

    getUsersByPermission({
      permissions: ['lead_view_view_own'],
    })
      .then((res) => {
        setFetchedUsers(res.users);
      })
      .catch(() => { });
    // eslint-disable-next-line
  }, []);


  const { t } = useTranslation();


  if (campaigns.isLoading || sources.isLoading)
    return (
      <div className="p-4">
        <LoadingScreen />
      </div>
    );


  return (
    <form className="flex flex-col gap-3" onSubmit={handleSubmit(handleButtonClick)}>
      <div>
        <Label text={t('Source')} />
        <div className="relative">
          <Controller
            name="source"
            control={control}
            render={({ field }) => (
              <Creatable<any, false>
                {...field}
                placeholder={t('Choose Source')}
                options={sources.data.sources.map((source: any) => ({
                  label: source.name,
                  value: source.id
                }))}
                value={
                  field.value
                    ? {
                      value: field.value,
                      label: sources.data.sources.find(
                        (source: any) => source.id === field.value
                      )?.name
                    }
                    : null
                }
                className="cursor-pointer font-inter-regular react-select-container"
                classNamePrefix="react-select"
                onChange={(selectedOption) => {
                  const selectedValue = selectedOption
                    ? selectedOption.value
                    : null;
                  field.onChange(selectedValue);

                }}
                styles={customStyles}
                isValidNewOption={() => false}
                menuPosition={"fixed"}
                menuPortalTarget={document.body}
              />
            )}
          />
          <RemoveFIlter
            condition={watch("source")}
            DeleteData={() => {
              reset({ ...getValues(), source: "" })
            }}
          />
        </div>
      </div>

      <div>
        <Label text={t('Campaign')} />
        <div className="relative">

          <Controller
            name="campaign"
            control={control}
            render={({ field }) => (
              <Creatable<any, false>
                {...field}
                placeholder={t('Choose Campaign')}
                options={campaigns.data.campaigns.map((campaign: any) => ({
                  label: campaign.name,
                  value: campaign.id
                }))}
                value={
                  field.value
                    ? {
                      value: field.value,
                      label: campaigns.data.campaigns.find(
                        (campaign: any) => campaign.id === field.value
                      )?.name
                    }
                    : null
                }
                className="cursor-pointer font-inter-regular react-select-container"
                classNamePrefix="react-select"
                onChange={(selectedOption) => {
                  const selectedValue = selectedOption
                    ? selectedOption.value
                    : null;
                  field.onChange(selectedValue);

                }}
                styles={customStyles}
                isValidNewOption={() => false}
                menuPosition={"fixed"}
                menuPortalTarget={document.body}
              />
            )}
          />
          <RemoveFIlter
            condition={watch("campaign")}
            DeleteData={() => {
              reset({ ...getValues(), campaign: "" })
            }}
          />
        </div>

      </div>
      <div>
        <Label text={t('Status')} />
        <div className="relative">

          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <Creatable<any, false>
                {...field}
                placeholder={t('Choose Status')}
                options={[
                  { label: `${t('Unprocessed')}`, value: "Unprocessed" },
                  { label: `${t('Not Reached')}`, value: "Not Reached" },
                  { label: `${t('In Contact')}`, value: "In Contact" },
                  { label: `${t('Lost')}`, value: "Lost" },
                  { label: `${t('Appointment')}`, value: "Appointment" },
                  { label: `${t('New customer')}`, value: "New Customer" },
                ]}
                value={
                  field.value
                    ? {
                      value: field.value,
                      label: t(field.value)
                    }
                    : null
                }
                className="cursor-pointer font-inter-regular react-select-container"
                classNamePrefix="react-select"
                onChange={(selectedOption) => {
                  const selectedValue = selectedOption
                    ? selectedOption.value
                    : null;
                  field.onChange(selectedValue);

                }}
                styles={customStyles}
                isValidNewOption={() => false}
                menuPosition={"fixed"}
                menuPortalTarget={document.body}
              />
            )}
          />
          <RemoveFIlter
            condition={watch("status")}
            DeleteData={() => {
              reset({ ...getValues(), status: "" })
            }}
          />
        </div>

      </div>
      <ConditionalRender condition={isAssignedLeads as boolean}>
        <div>
          <Label text={t('Worker')} />
          <div className="relative">

            <Controller
              name="worker"
              control={control}
              render={({ field }) => (
                <Creatable<any, false>
                  {...field}
                  placeholder={t('Choose Salesman')}
                  options={
                    (
                      fetchedUsers as unknown as Array<{
                        name: string;
                        id: string;
                      }>
                    )?.map?.((user) => ({
                      label: user?.name,
                      value: user?.id,
                    })) ?? []
                  }
                  value={
                    field?.value
                      ? {
                        value: field?.value,
                        label:
                          (
                            fetchedUsers?.find?.(
                              (user: any) => user.id === field.value
                            ) as { name: string } | undefined
                          )?.name || field.value,
                      }
                      : null
                  }
                  className="cursor-pointer font-inter-regular react-select-container"
                  classNamePrefix="react-select"
                  onChange={(selectedOption) => {
                    const selectedValue = selectedOption
                      ? selectedOption.value
                      : null;
                    field.onChange(selectedValue);

                  }}
                  styles={customStyles}
                  isValidNewOption={() => false}
                  menuPosition={"fixed"}
                  menuPortalTarget={document.body}
                />
              )}
            />
            <RemoveFIlter
              condition={watch("worker")}
              DeleteData={() => {

                reset({ ...getValues(), worker: "" })
              }}
            />
          </div>
        </div>
      </ConditionalRender>
      <Controller
        name="from_date_of_creation"
        control={control}
        render={({ field }) => (
          <PrimaryDatePicker
            label={t('From date of creation')}
            setStartDate={field.onChange}
            startDate={field.value}
            wrapperClassName="!left-[2px]"
            DeleteData={() => reset({ ...getValues(), from_date_of_creation: null })}
            filterCondition={watch("from_date_of_creation")}
          />
        )}
      />

      <Controller
        name="to_date_of_creation"
        control={control}
        render={({ field }) => (
          <PrimaryDatePicker
            label={t('To date of creation')}
            setStartDate={field.onChange}
            startDate={field.value}
            wrapperClassName="!left-[2px]"
            DeleteData={() => reset({ ...getValues(), to_date_of_creation: null })}
            filterCondition={watch("to_date_of_creation")}
          />
        )}
      />


      <div className="pt-5">
        <div className="flex flex-row justify-end gap-3">
          <IconButton
            secondary={true}
            className="w-[110px]"
            onClick={ResetData}
            type="button"
          >
            {t('Reset')}
          </IconButton>
          <IconButton className="w-[110px]" type="submit">
            {t('Confirm')}
          </IconButton>
        </div>
      </div>
    </form>
  );
};

export default FilterTableDropdown;